import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  FormHelperText,
  Tooltip,
  Divider,
  useTheme,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useTranslation } from "react-i18next";

import { RegistrationFormFields } from "helpers/formFields";
import { MIN_CL, MAX_CL, CL_DEFAULT, SUPPLIER } from "codes";
import { currencyFormatter } from "helpers/currencyFormatter";
import { useGetPartnerEligibleCountiesQuery } from "api/api-misc";
import useIsMobile from "hooks/useIsMobile";
import styles from "./cl.module.scss";
import CLInputs from "../TradingRelation/CLInputs";
// import valueCleaner from "helpers/valueCleaner";
// import CLSlider from "./Slider";

const SubmitCL = () => {
  const valueCleaner = (val: string | number | undefined | null): number => {
    if (typeof val === "number") return Math.floor(val);
    if (typeof val !== "string") return 0;
    return Number(val.split(".")[0]?.replaceAll(",", "") || "0");
  };
  const { values, setFieldValue, errors } =
    useFormikContext<RegistrationFormFields>();
  const { list = [], requested_amount, companyType } = values;
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { t } = useTranslation();

  const { data: buyerEligibleCountries } =
    useGetPartnerEligibleCountiesQuery("buyer");

  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [allocations, setAllocations] = useState<{ [key: number]: number }>(
    {}
  );
  const [isLoading, setIsLoading] = useState(true);

  const effectiveRequestedAmount = requested_amount || CL_DEFAULT;
  const totalAllocated = Object.values(allocations).reduce(
    (sum, val) => sum + val,
    0
  );

  const isRequestedAmountError =
    requested_amount < MIN_CL || requested_amount > MAX_CL;

  useEffect(() => {
    if (!list.length || manuallyEdited) return;

    const equal = Math.floor(effectiveRequestedAmount / list.length);
    const remainder = effectiveRequestedAmount - equal * list.length;

    const newAllocations: { [key: number]: number } = {};
    const updatedList = [...list];

    list.forEach((buyer, index) => {
      const partner_requested_amount = equal + (index === 0 ? remainder : 0);
      newAllocations[index] = partner_requested_amount;
      updatedList[index] = {
        ...buyer,
        partner_requested_amount,
      };
    });

    setAllocations(newAllocations);
    setFieldValue("list", updatedList);
  }, [effectiveRequestedAmount, list.length, manuallyEdited]);

  useEffect(() => {
    if (Boolean(list.length)) setIsLoading(false);
    else setIsLoading(true);
  }, [list]);

  const handleChange = (index: number, val: string) => {
    const partner_requested_amount = valueCleaner(val);
    setManuallyEdited(true);

    const updatedAlloc = { ...allocations, [index]: partner_requested_amount };
    setAllocations(updatedAlloc);

    const updatedList = [...list];
    updatedList[index] = {
      ...updatedList[index],
      partner_requested_amount,
    };
    setFieldValue("list", updatedList);
  };

  const isIneligibleCountry = (country: string) =>
    !buyerEligibleCountries?.some(
      (c) => c.name.toLowerCase() === country.toLowerCase()
    );

  const CURRENCY = "USD";

  return (
    <Grid container spacing={isMobile ? 0 : 2}>
      {companyType?.toLowerCase() === SUPPLIER.toLowerCase() ? (
        <>
          {/* // Factoring (Supplier) Flow */}
          <Grid item xs={12}>
            <Typography
              variant="h3"
              textAlign="center"
              fontWeight="bolder"
              fontSize={isMobile ? "1.5em" : "2em"}
            >
              {`${t("credit-limit-request")}`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              sx={{
                color: theme.palette.tertiary.main,
                fontWeight: "bolder",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.8em",
                },
              }}
            >
              {t("about-to-submit-clr")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="h3"
              fontWeight="bold"
              fontSize={isMobile ? "1.2em" : "1.5em"}
              textAlign={isMobile ? "center" : "start"}
              mt={2}
            >
              {t("submit-your-buyer-info")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {!isMobile && (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Typography variant="h4" fontWeight="bold">
                      {t("company-full-legal-name")}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h4" fontWeight="bold">
                      {t("registration-number")}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Typography variant="h4" fontWeight="bold">
                          {t("country")}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Tooltip
                          title={
                            <Grid container maxHeight="40vh" overflow="scroll">
                              <Typography variant="h3" fontSize="1.2em" fontWeight="bold">
                                {t("authorised-buyer-countries")}
                              </Typography>
                              {buyerEligibleCountries?.map((country) => (
                                <Grid item xs={12} key={country.id}>
                                  {country.name}
                                </Grid>
                              ))}
                            </Grid>
                          }
                        >
                          <InfoIcon />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={3}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Typography variant="h4" fontWeight="bold">
                          {t("select-credit-limit")}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Tooltip
                          title={`Please request your credit limit within ${CURRENCY}${currencyFormatter(
                            { amount: MIN_CL, decimalPlace: 0 }
                          )} - ${CURRENCY}${currencyFormatter({
                            amount: MAX_CL,
                            decimalPlace: 0,
                          })}`}
                        >
                          <InfoIcon />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider />
              </>
            )}
            {list.map((buyer, index) => {
              const isInEligible = isIneligibleCountry(buyer.country);
              return (
                <CLInputs
                  key={buyer.registration_number}
                  index={index}
                  isInEligible={isInEligible}
                />
              );
            })}
          </Grid>
        </>
      ) : (
        <>
          {/* Reverse Factoring (Buyer) Flow */}
          <Grid item xs={12}>
            <Typography variant="h3">Credit Limit Allocation</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <NumericFormat
              displayType="input"
              thousandSeparator
              customInput={TextField}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">USD</InputAdornment>
                ),
                className: isRequestedAmountError ? styles.invalidAmount : undefined,
              }}
              value={requested_amount ?? CL_DEFAULT}
              onValueChange={(vals) => {
                setFieldValue("requested_amount", valueCleaner(vals.value));
                setManuallyEdited(false);
              }}
              error={isRequestedAmountError}
            />
            {isRequestedAmountError && (
              <FormHelperText error>
                {errors?.requested_amount as string}
              </FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h4" fontWeight="bold">
              Distribute Credit Limit to Partners (Total must equal requested amount)
            </Typography>

            {list.map((buyer, index) => (
              <Grid
                container
                key={buyer.registration_number}
                spacing={2}
                alignItems="center"
                mt={1}
                mb={1}
              >
                <Grid item xs={4}>
                  <Typography>{buyer.name}</Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <NumericFormat
                    customInput={TextField}
                    thousandSeparator
                    fullWidth
                    value={allocations[index] ?? ""}
                    onChange={(e) => handleChange(index, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">USD</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            ))}

            <Typography
              mt={2}
              fontWeight="bold"
              color={
                totalAllocated === effectiveRequestedAmount
                  ? "green"
                  : totalAllocated > effectiveRequestedAmount
                    ? "error"
                    : "orange"
              }
            >
              {totalAllocated === effectiveRequestedAmount
                ? `Total allocated: ${currencyFormatter({ amount: totalAllocated })} — Allocation is balanced.`
                : totalAllocated > effectiveRequestedAmount
                  ? `Total allocated: ${currencyFormatter({
                    amount: totalAllocated,
                  })} / ${currencyFormatter({
                    amount: effectiveRequestedAmount,
                  })} — Please reduce the allocation to match the requested amount.`
                  : `Total allocated: ${currencyFormatter({
                    amount: totalAllocated,
                  })} / ${currencyFormatter({
                    amount: effectiveRequestedAmount,
                  })} — Please increase the allocation to match the requested amount.`}
            </Typography>

          </Grid>
        </>
      )}
    </Grid>
  );
};
export default SubmitCL;
